import { DataTypeEnum } from "../../common/enums/DataTypeEnum";
import { RulesEngineActionTypeEnum } from "../../common/enums/RulesEngineActionTypeEnum";
import { RulesEngineRuleCustomerTypeEnum } from "../../common/enums/RulesEngineRuleCustomerTypeEnum";
import { RulesEngineRuleRunTypeEnum } from "../../common/enums/RulesEngineRuleRunTypeEnum";
import { RulesEngineRuleScopeEnum } from "../../common/enums/RulesEngineRuleScopeEnum";
import { RulesEngineRuleTypeEnum } from "../../common/enums/RulesEngineTypeEnum";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { DRCXAvailableObjects } from "../../common/models/drcx/DRCXAvailableObjects";
import { IDRCXAvailableObjectsDTO } from "../../common/models/drcx/DRCXAvailableObjectsDTO";
import { IDRCXConfigurationSectionObjectDTO } from "../../common/models/drcx/DRCXConfigurationSectionObjectDTO";
import restService from "../rest-service/RestService";
import { ITrendDataResultsModel } from "../trending-data-service/Models";
import { ExpressionTypeEnum, IAvailableActionDTO, IAvailableNotificationInfoDTO, IConditionKeyDTO, IDisableScheduleDTO, IEditActionInstanceDTO, IEditRuleDTO, IEditRuleInstanceDTO, IGetRulesEngineRulesQueryParams, IRuleFactDTO, IRulesEngineAvailableRulesInfoModel, IRulesEngineCompilationDiagnosticDTO, IRulesEngineRuleInstanceQueryModel, IRulesEngineRuleInstanceSummaryModel, IRulesEngineRuleInstanceUsageModel, IRulesEngineRuleModel, IRulesEngineRuleUsageModel, IRulesEngineSimulationInfo, SimulateRuleQueryParams } from "./Models";

export const NewRuleID = -1;
export const NewRuleInstanceID = -1;

const RulesEngineDataService = {

    // Rules
    getRulesEngineRule: async function (
        ruleID: number,
        signal?: AbortSignal
    ): Promise<IEditRuleDTO> {
        const path: string = `/rules-engine/rules/${ruleID}`;

        const result = await restService.get<IEditRuleDTO>(path, signal);

        return result.data;
    },

    getRulesEngineRules: async function (
        ruleID?: number | undefined,
        ruleType?: RulesEngineRuleTypeEnum,
        runType?: RulesEngineRuleRunTypeEnum,
        customerType?: RulesEngineRuleCustomerTypeEnum,
        signal?: AbortSignal
    ): Promise<Array<IRulesEngineRuleModel>> {
        const path: string = `v2/rules-engine/rules`;

        const queryParams = {
            ruleID,
            ruleType,
            runType,
            customerType
        } as IGetRulesEngineRulesQueryParams;

        const result = await restService.get<Array<IRulesEngineRuleModel>, IGetRulesEngineRulesQueryParams>(path, signal, queryParams);

        return result.data;
    },

    getRulesEngineRuleUsage: async function (
        contextGroupID: number,
        ruleID: number,
        signal?: AbortSignal
    ): Promise<Array<IRulesEngineRuleUsageModel>> {
        const path: string = `groups/${contextGroupID}/rules-engine/rule-usage/${ruleID}`;

        const result = await restService.get<Array<IRulesEngineRuleUsageModel>>(path, signal);

        return result.data;
    },

    saveRulesEngineRule: async function (
        groupID: number,
        model: IEditRuleDTO,
        signal?: AbortSignal
    ): Promise<IEditRuleDTO> {
        const path: string = `groups/${groupID}/rules-engine/rule`;

        const result = await restService.post<IEditRuleDTO, IEditRuleDTO>(
            path,
            model,
            signal
        );

        return result.data;
    },


    deleteRulesEngineRule: async function (
        groupID: number,
        model: IEditRuleDTO,
        signal?: AbortSignal
    ): Promise<void> {
        const path: string = `groups/${groupID}/rules-engine/rule`;

        await restService.delete<IEditRuleDTO, undefined>(
            path,
            signal,
            undefined,
            model
        );
    },

    // Rule Instances
    getRulesEngineRuleInstances: async function (
        contextGroupID: number,
        queryParameters: IRulesEngineRuleInstanceQueryModel,
        signal?: AbortSignal
    ): Promise<IRulesEngineRuleInstanceSummaryModel> {
        const path: string = `v2/groups/${contextGroupID}/rules-engine/rules-instances-summary`;

        const result = await restService.post<IRulesEngineRuleInstanceQueryModel, IRulesEngineRuleInstanceSummaryModel>(
            path,
            queryParameters,
            signal
        );

        return result.data;
    },

    getRulesEngineRuleInstanceUsage: async function (
        contextGroupID: number,
        ruleInstanceID: number,
        signal?: AbortSignal
    ): Promise<Array<IRulesEngineRuleInstanceUsageModel>> {
        const path: string = `groups/${contextGroupID}/rules-engine/rule-instances-usage/${ruleInstanceID}`;

        const result = await restService.get<Array<IRulesEngineRuleInstanceUsageModel>>(path, signal);

        return result.data;
    },

    getRulesEngineRuleInstance: async function (
        ruleInstanceID: number,
        signal?: AbortSignal
    ): Promise<IEditRuleInstanceDTO> {
        const path: string = `rules-engine/rule-instances/${ruleInstanceID}`;

        const result = await restService.get<IEditRuleInstanceDTO>(path, signal);

        return result.data;
    },

    getRulesEngineNewRuleInstance: async function (
        ruleID: number,
        signal?: AbortSignal
    ): Promise<IEditRuleInstanceDTO> {
        const path: string = `rules-engine/rules/${ruleID}/new-instance`;

        const result = await restService.get<IEditRuleInstanceDTO>(path, signal);

        return result.data;
    },

    saveRulesEngineRuleInstance: async function (
        groupID: number,
        model: IEditRuleInstanceDTO,
        signal?: AbortSignal
    ): Promise<IEditRuleInstanceDTO> {
        const path: string = `groups/${groupID}/rules-engine/rule-instance`;

        const result = await restService.post<IEditRuleInstanceDTO, IEditRuleInstanceDTO>(
            path,
            model,
            signal
        );

        return {
            ...model,
            id: result.data.id
        };
    },

    deleteRulesEngineRuleInstance: async function (
        groupID: number,
        model: IEditRuleInstanceDTO,
        signal?: AbortSignal
    ): Promise<void> {
        const path: string = `groups/${groupID}/rules-engine/rule-instance`;

        await restService.delete<IEditRuleInstanceDTO, undefined>(
            path,
            signal,
            undefined,
            model
        );
    },

    enableRulesEngineRuleInstance: async function (
        groupID: number,
        ruleInstanceId: number,
        signal?: AbortSignal
    ): Promise<void> {

        const path: string = `groups/${groupID}/rules-engine/rule-instances/${ruleInstanceId}/instance-enable`;

        await restService.put(path, null, signal);
    },

    disableRulesEngineRuleInstance: async function (
        groupID: number,
        ruleInstanceId: number,
        signal?: AbortSignal
    ): Promise<void> {

        const path: string = `groups/${groupID}/rules-engine/rule-instances/${ruleInstanceId}/instance-disable`;

        await restService.put(path, null, signal);
    },

    enableRulesEngineRuleInstanceObject: async function (
        groupID: number,
        ruleInstanceID: number,
        objectType: eLynxObjectTypeEnum,
        objectID: number,
        signal?: AbortSignal
    ): Promise<void> {

        const path: string = `groups/${groupID}/rules-engine/rule-instances/${ruleInstanceID}/enable`;

        await restService.put<void, { objectType: eLynxObjectTypeEnum, objectID: number; }, void>(
            path,
            undefined,
            signal,
            { objectType: objectType, objectID: objectID }
        );
    },

    disableRulesEngineRuleInstanceObject: async function (
        groupID: number,
        ruleInstanceID: number,
        objectType: eLynxObjectTypeEnum,
        objectID: number,
        scheduleModel: IDisableScheduleDTO,
        signal?: AbortSignal
    ): Promise<void> {

        const path: string = `groups/${groupID}/rules-engine/rule-instances/${ruleInstanceID}/disable`;
        
        await restService.put<IDisableScheduleDTO, { objectType: eLynxObjectTypeEnum, objectID: number; }, void>(
            path,
            scheduleModel,
            signal,
            { objectType: objectType, objectID: objectID }
        );
    },

    getRulesEngineRuleInstanceObjectDisableSchedule: async function (
        groupID: number,
        ruleInstanceID: number,
        objectType: eLynxObjectTypeEnum,
        objectID: number,
        signal?: AbortSignal
    ): Promise<IDisableScheduleDTO> {

        const path: string = `groups/${groupID}/rules-engine/rule-instances/${ruleInstanceID}/disableSchedule`;

        const result = await restService.get<IDisableScheduleDTO, { objectType: eLynxObjectTypeEnum, objectID: number; }>(
            path,
            signal,
            {
                objectType: objectType,
                objectID: objectID
            }
        );

        return result.data;
    },

    getRulesEngineRuleConditionKeys: async function (
        groupID: number,
        includeWells?: boolean,
        signal?: AbortSignal
    ): Promise<Array<IConditionKeyDTO>> {

        const path: string = `groups/${groupID}/rules-engine/available-rule-condition-items`;

        // default to true
        if (includeWells === undefined) {
            includeWells = true;
        }

        const result = await restService.get<{ tatKeys: Array<{ id: number, displayText: string, dataType: DataTypeEnum; }>; }, { includeWells: boolean; }>(path, signal, { includeWells: includeWells });

        return result.data.tatKeys.map(tat => {
            return {
                equatableKey: `${tat.id}`,
                label: tat.displayText,
                expressionType: tat.dataType === DataTypeEnum.String
                    ? ExpressionTypeEnum.TextTagData
                    : ExpressionTypeEnum.NumericTagData
            };
        });
    },

    getRuleTemplateCompilationDiagnostics: async function (
        ruleTemplate: string,
        signal?: AbortSignal
    ): Promise<Array<IRulesEngineCompilationDiagnosticDTO>> {

        const path: string = "rules-engine/compile-template";

        const result = await restService.post<{ template: string; }, Array<IRulesEngineCompilationDiagnosticDTO>>(
            path,
            { template: ruleTemplate },
            signal
        );

        return result.data;
    },

    getRulesEngineAvailableObjects: async function (
        groupID: number,
        facts: Array<IRuleFactDTO>,
        includeDescendentGroups: boolean = true,
        signal?: AbortSignal
    ): Promise<DRCXAvailableObjects> {
        const path: string = `groups/${groupID}/rules-engine/available-objects`;

        const response = await restService
            .post<{ includeDevices: boolean, includeTanks: boolean, includeWells: boolean, includeDescendentGroups: boolean, facts: Array<IRuleFactDTO>; }, IDRCXAvailableObjectsDTO>(
                path,
                {
                    includeDevices: true,
                    includeTanks: true,
                    includeWells: true,
                    includeDescendentGroups: includeDescendentGroups,
                    facts: facts
                },
                signal
            );

        return new DRCXAvailableObjects(response.data);
    },

    getRulesEngineAvailableActions: async function (
        groupID: number,
        ruleType?: RulesEngineRuleTypeEnum,
        signal?: AbortSignal
    ): Promise<Array<IAvailableActionDTO>> {
        const path: string = `groups/${groupID}/rules-engine/available-actions`;

        const response = await restService.get<Array<IAvailableActionDTO>, { ruleType?: RulesEngineRuleTypeEnum; }>(
            path,
            signal,
            { ruleType: ruleType }
        );

        return response.data;
    },

    getRulesEngineAvailableRulesInfo: async function (
        signal?: AbortSignal
    ): Promise<IRulesEngineAvailableRulesInfoModel> {

        const path: string = "rules-engine/available-rules-info";

        const result = await restService.get<IRulesEngineAvailableRulesInfoModel>(path, signal);

        return result.data;
    },


    getRulesEngineAvailableNotificationInfo: async function (
        groupID: number,
        signal?: AbortSignal
    ): Promise<IAvailableNotificationInfoDTO> {

        const path: string = `groups/${groupID}/rules-engine/available-notification-info`;

        const result = await restService.get<IAvailableNotificationInfoDTO>(path, signal);

        return result.data;

        //  return new AvailableNotificationInfo(result.data);
    },



    // Simulation
    getSimulationSession: async function (
        groupID: number,
        simulationSessionID: string,
        signal?: AbortSignal
    ): Promise<any> {
        const path: string = `groups/${groupID}/rules-engine/simulation-session`;

        const result = await restService.post<{ id: string; }, any>(
            path,
            {
                id: simulationSessionID
            },
            signal
        );

        return result.data;
    },

    simulateRule: async function (
        groupID: number,
        // targetObject: IeLynxObject,
        // dataRequestOptions: IRulesEngineSimulatedDataRequestModel,
        queryParams: SimulateRuleQueryParams,
        signal?: AbortSignal
    ): Promise<string> {
        const path: string = `groups/${groupID}/rules-engine/simulate-rule`;

        const result = await restService.post<SimulateRuleQueryParams, string>(
            path,
            queryParams,
            signal
        );

        return result.data;
    },

    getSimulationInfo: async function (
        groupID: number,
        signal?: AbortSignal
    ): Promise<IRulesEngineSimulationInfo> {
        const path: string = `groups/${groupID}/rules-engine/simulation-info`;

        const result = await restService.get<IRulesEngineSimulationInfo>(
            path,
            signal
        );

        return result.data;
    },

    getSimulationChart: async function (
        contextGroupID: number,
        queryParams: SimulateRuleQueryParams,
        signal?: AbortSignal
    ): Promise<ITrendDataResultsModel> {

        const path: string = `groups/${contextGroupID}/rules-engine/simulation-chart`;

        const result = await restService.post<SimulateRuleQueryParams, ITrendDataResultsModel>(
            path,
            queryParams,
            signal
        );

        return result.data;
    },


    createDefaultRule: function (): IEditRuleDTO {

        return {
            id: NewRuleID,
            ruleType: RulesEngineRuleTypeEnum.CustomDiagnostic,
            ruleScope: RulesEngineRuleScopeEnum.Customer,
            ruleName: "New Rule",
            template: "",
            ruleDataDefinition: {
                facts: new Array<IRuleFactDTO>(),
                canEvaluateWithoutData: false
            },
            ruleRunType: RulesEngineRuleRunTypeEnum.TriggeredOnly,
            description: "",
            logicText: "",
            eventStartRuleLogic: "{\"conditionGroups\":[{\"conditions\":[{}]}],\"enabled\":true}",
            eventEndRuleLogic: "{\"conditionGroups\":[{\"conditions\":[{}]}],\"enabled\":false}",
        };
    },

    createDefaultRuleInstance: function (): IEditRuleInstanceDTO {

        const defaultRule = RulesEngineDataService.createDefaultRule();

        return {
            id: NewRuleInstanceID,
            name: "New Rule Instance",
            active: true,
            objectConfiguration: new Array<IDRCXConfigurationSectionObjectDTO>(),
            mergedRuleDataDefinition: {
                facts: new Array<IRuleFactDTO>(),
                canEvaluateWithoutData: false
            },
            tumblingWindow: "00:10:00.000",
            actions: new Array<IEditActionInstanceDTO>(
                RulesEngineDataService.createDefaultActionInstance()
            ),
            schedules: new Array<IDisableScheduleDTO>(),

            ruleID: defaultRule.id,
            ruleName: defaultRule.ruleName,
            ruleDescription: defaultRule.description,
            ruleLogicText: defaultRule.logicText,
            ruleType: defaultRule.ruleType,
            ruleScope: defaultRule.ruleScope,
            ruleRunType: defaultRule.ruleRunType,
            ruleTemplate: defaultRule.template,
            ruleDataDefinition: defaultRule.ruleDataDefinition
        };
    },

    createDefaultActionInstance: function (): IEditActionInstanceDTO {
        return {
            id: -1,
            actionType: RulesEngineActionTypeEnum.DiagnositicEvent,
            outputObjectConfiguration: undefined,
            actionConfiguration: undefined
        };
    }
};

export default RulesEngineDataService;